import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authService } from '../services/auth';
import toast from 'react-hot-toast';

export const AuthContext = createContext();

/**
 * Permissions now come from the server on every profile load
 * (see /api/auth/profile), so a role change takes effect on the next
 * refresh rather than needing the user to log out and back in.
 *
 * Nothing here is a security boundary. Hiding a button stops an honest
 * user wandering into a 403; the backend decorator is what actually
 * enforces the rule. Never rely on `can()` alone to protect anything.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));

  const loadUser = useCallback(async () => {
    const currentToken = localStorage.getItem('access_token');
    if (!currentToken) {
      setLoading(false);
      return;
    }

    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      const status = error.response?.status;

      if (status === 401 || status === 403) {
        if (status === 403) {
          toast.error(error.response?.data?.message || 'Account suspended');
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setToken(null);
        setUser(null);
      }
      // Network errors / 500s: stay logged in
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /**
   * Re-read the profile without a full page reload. Call this after an
   * admin changes their own role or permissions so the UI catches up.
   */
  const refreshPermissions = useCallback(async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
      return null;
    }
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      const { access_token, refresh_token, user: loggedIn } = response;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      setToken(access_token);

      // The login response predates the RBAC work and carries no
      // permissions. Pull the full profile so `can()` is populated before
      // the first render after redirect.
      let full = loggedIn;
      try {
        full = await authService.getProfile();
      } catch {
        // Fall back to the login payload; the next profile load will fix it.
      }
      setUser(full);

      const displayName = full.first_name || full.username;
      const roleName = full.role_name || full.user_type;
      toast.success(
        full.user_type === 'admin'
          ? 'Welcome back, Admin!'
          : `Welcome back, ${displayName}${roleName && roleName !== 'bidder' ? ` (${roleName})` : ''}!`
      );

      return { success: true, user: full, landingPath: full.landing_path || '/dashboard' };
    } catch (error) {
      console.error('Login error:', error);

      let errorMsg = 'Login failed. Please check your credentials.';

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 401:
            errorMsg = data?.message || 'Invalid username or password';
            break;
          case 403:
            if (
              data?.message?.toLowerCase().includes('verify') ||
              data?.message?.toLowerCase().includes('verified') ||
              data?.message?.toLowerCase().includes('approval') ||
              data?.message?.toLowerCase().includes('verification')
            ) {
              errorMsg =
                'Your account is pending admin approval. Please check back later.';
            } else if (data?.message?.toLowerCase().includes('suspended')) {
              errorMsg =
                'Your account has been suspended. Please contact support for assistance.';
            } else {
              errorMsg = data?.message || 'Access denied. Please contact support.';
            }
            break;
          case 404:
            errorMsg = data?.message || 'User not found';
            break;
          case 429:
            errorMsg = 'Too many login attempts. Please try again later.';
            break;
          case 500:
            errorMsg = 'Server error. Please try again later.';
            break;
          default:
            errorMsg = data?.message || data?.error || `Login failed (${status})`;
        }
      } else if (error.request) {
        errorMsg = 'Unable to connect to server. Please check your internet connection.';
      } else {
        errorMsg = error.message || 'An unexpected error occurred';
      }

      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      const message =
        response.message ||
        'Registration successful. Your account is pending admin approval.';
      toast.success(message);
      return { success: true, message };
    } catch (error) {
      console.error('Registration error:', error);

      let errorMsg = 'Registration failed. Please try again.';

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            if (data.errors) {
              errorMsg =
                Object.values(data.errors).flat().join(', ') ||
                data?.message ||
                'Invalid registration data';
            } else {
              errorMsg = data?.message || 'Invalid registration data';
            }
            break;
          case 409:
            if (data?.message?.toLowerCase().includes('email')) {
              errorMsg =
                'Email already registered. Please use a different email or try logging in.';
            } else if (data?.message?.toLowerCase().includes('username')) {
              errorMsg = 'Username already taken. Please choose a different username.';
            } else {
              errorMsg = data?.message || 'User already exists';
            }
            break;
          case 422:
            errorMsg = data?.message || 'Validation failed. Please check your input.';
            break;
          case 500:
            errorMsg = 'Server error. Please try again later.';
            break;
          default:
            errorMsg = data?.message || data?.error || `Registration failed (${status})`;
        }
      } else if (error.request) {
        errorMsg = 'Unable to connect to server. Please check your internet connection.';
      } else {
        errorMsg = error.message || 'An unexpected error occurred';
      }

      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const refreshToken = async () => {
    try {
      const stored = localStorage.getItem('refresh_token');
      if (!stored) throw new Error('No refresh token');

      const response = await authService.refreshToken(stored);
      const { access_token } = response;

      localStorage.setItem('access_token', access_token);
      setToken(access_token);
      return access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      toast.error(
        error.response?.status === 401
          ? 'Your session has expired. Please login again.'
          : 'Session expired. Please login again.'
      );
      throw error;
    }
  };

  // ---------------------------------------------------------------
  // Permission helpers
  // ---------------------------------------------------------------

  const permissions = useMemo(
    () => new Set(user?.permissions || []),
    [user]
  );

  const isAdmin = user?.user_type === 'admin' || user?.role_slug === 'admin';

  /** True if the user holds every code passed. */
  const can = useCallback(
    (...codes) => {
      if (!user) return false;
      if (isAdmin) return true; // mirrors the backend short-circuit
      return codes.flat().every((code) => permissions.has(code));
    },
    [user, isAdmin, permissions]
  );

  /** True if the user holds at least one of the codes passed. */
  const canAny = useCallback(
    (...codes) => {
      if (!user) return false;
      if (isAdmin) return true;
      return codes.flat().some((code) => permissions.has(code));
    },
    [user, isAdmin, permissions]
  );

  const value = {
    user,
    loading,
    token,
    login,
    register,
    logout,
    refreshToken,
    refreshPermissions,

    isAuthenticated: !!user,
    isAdmin,

    permissions: user?.permissions || [],
    can,
    canAny,

    roleSlug: user?.role_slug || user?.user_type || null,
    roleName: user?.role_name || user?.user_type || null,
    landingPath: user?.landing_path || '/dashboard',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
